import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { usePremium } from '../context/PremiumContext.jsx';
import api from '../api.js';

const FEATURES = [
  {
    title: 'AI Course Generation',
    desc: 'Point the AI at a city and get a custom course with real bars, photos, and routes.',
  },
  {
    title: 'Unlimited Stories',
    desc: 'Post photo highlights from every hole.',
  },
  {
    title: 'Game History',
    desc: 'Access the full archive of your past rounds.',
  },
  {
    title: 'Advanced Stats',
    desc: 'Variance, consistency, per-venue heatmaps.',
  },
  {
    title: 'Custom Themes',
    desc: 'Violet, Ocean, and Berry theme packs.',
  },
  {
    title: 'Priority Support',
    desc: 'Skip the line for help and feature requests.',
  },
];

export default function Premium() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const { premium, unlimited, refreshStatus } = usePremium();
  const [busy, setBusy] = useState(null);
  const [banner, setBanner] = useState(null); // { kind: 'success' | 'canceled' | 'error', text }
  const confirmedRef = useRef(false);

  // Handle the redirect back from Stripe Checkout. The entire body is wrapped
  // in try/catch so a thrown error inside an async handler can never crash
  // the React subtree to a blank page — at worst the user sees an error
  // banner explaining what failed.
  //
  // We deliberately do NOT use a cancelled-flag cleanup pattern here: React
  // 18 silently no-ops setState on unmounted components, and StrictMode's
  // double-invoke would otherwise cancel the very first confirm and skip
  // the second one (because confirmedRef has already flipped). The
  // confirmedRef + session_id dedup is enough to prevent double-confirms.
  useEffect(() => {
    const safeSetBanner = (b) => {
      setBanner({
        kind: b?.kind || 'error',
        text:
          typeof b?.text === 'string' && b.text ? b.text : 'Something went wrong',
      });
    };

    try {
      const params = new URLSearchParams(loc.search);
      const success = params.get('success');
      const canceled = params.get('canceled');
      const sessionId = params.get('session_id');

      if (canceled === 'true') {
        safeSetBanner({
          kind: 'canceled',
          text: 'Payment canceled — no charge was made.',
        });
        try {
          window.history.replaceState({}, '', loc.pathname);
        } catch {}
        return;
      }

      // Dedup against the session_id rather than a plain bool so a StrictMode
      // double-invoke (with a fresh confirmedRef on remount) still works.
      if (
        success === 'true' &&
        sessionId &&
        user?.uid &&
        confirmedRef.current !== sessionId
      ) {
        confirmedRef.current = sessionId;
        setBusy('confirming');

        (async () => {
          try {
            const res = await api.confirmCheckout({
              session_id: sessionId,
              userId: user.uid,
            });
            if (res && res.success) {
              safeSetBanner({
                kind: 'success',
                text: 'Payment confirmed — Premium unlocked. 🎉',
              });
              try {
                await refreshStatus();
              } catch (refreshErr) {
                // Refresh failed but the purchase is confirmed — just log.
                console.warn('refreshStatus after confirm failed', refreshErr);
              }
            } else {
              safeSetBanner({
                kind: 'error',
                text:
                  (res && (res.error || res.detail)) ||
                  'Could not confirm payment',
              });
            }
          } catch (err) {
            // Defensively read error fields — never trust err to be an Error
            // instance (could be undefined, a string, an object, etc.)
            const message =
              (err && typeof err === 'object' && (err.message || err.error)) ||
              (typeof err === 'string' ? err : null) ||
              'Could not confirm payment';
            safeSetBanner({
              kind: 'error',
              text: String(message).slice(0, 240),
            });
            console.error('confirmCheckout failed', err);
          } finally {
            setBusy(null);
            try {
              window.history.replaceState({}, '', loc.pathname);
            } catch {}
          }
        })();
      }
    } catch (outerErr) {
      // Last-resort guard so the effect never throws synchronously.
      console.error('Premium effect crashed', outerErr);
      safeSetBanner({
        kind: 'error',
        text: 'Could not finish setting up the Premium screen.',
      });
    }
  }, [loc.search, loc.pathname, user?.uid, refreshStatus]);

  const buy = async (productId) => {
    if (!user?.uid) {
      setBanner({ kind: 'error', text: 'Sign in first to purchase Premium.' });
      return;
    }
    setBusy(productId);
    setBanner(null);
    try {
      const res = await api.createCheckout({ userId: user.uid, productId });
      if (res && res.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error((res && (res.error || res.detail)) || 'No checkout URL returned');
    } catch (err) {
      const message =
        (err && typeof err === 'object' && (err.message || err.error)) ||
        (typeof err === 'string' ? err : null) ||
        'Checkout failed';
      setBanner({ kind: 'error', text: String(message).slice(0, 240) });
      setBusy(null);
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1>Premium</h1>
        <div className="spacer" />
      </div>

      <div className="premium-hero">
        <div className="crown">👑</div>
        <h1>Pub Golf Premium</h1>
        <div className="price">{premium ? 'Unlocked' : '$4.99 one-time'}</div>
      </div>

      {banner && (
        <div className={`premium-banner premium-banner-${banner.kind}`}>
          {banner.text}
        </div>
      )}

      <div className="feature-list">
        {FEATURES.map((f) => (
          <div className="feature" key={f.title}>
            <span className="check">✓</span>
            <div>
              <h4>{f.title}</h4>
              <p>{f.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {busy === 'confirming' && (
        <div className="text-center text-muted" style={{ marginBottom: '1rem' }}>
          Confirming payment with Stripe…
        </div>
      )}

      {!premium && (
        <button
          className="btn btn-primary btn-block btn-lg"
          onClick={() => buy('premium')}
          disabled={busy != null}
        >
          {busy === 'premium' ? 'Opening Stripe…' : 'Unlock Premium — $4.99'}
        </button>
      )}

      {premium && !unlimited && (
        <button
          className="btn btn-primary btn-block btn-lg"
          style={{ marginTop: '0.75rem' }}
          onClick={() => buy('unlimited')}
          disabled={busy != null}
        >
          {busy === 'unlimited' ? 'Opening Stripe…' : 'Unlimited Generations — $3.99'}
        </button>
      )}

      <button
        className="btn btn-ghost btn-block"
        style={{ marginTop: '0.75rem' }}
        onClick={refreshStatus}
      >
        Restore Purchases
      </button>

      <div className="text-center text-muted" style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
        One-time purchase. No subscription.
      </div>
    </div>
  );
}
